<template>
  <main class="checkout-page">
    <section class="checkout-hero" :style="heroStyle">
      <div class="checkout-hero__shade"></div>
      <div class="checkout-hero__content">
        <img v-if="visuals.logo" class="checkout-hero__logo" :src="visuals.logo" :alt="visuals.name">
        <div>
          <div class="checkout-hero__label">{{ $t('checkout.donation_label') }}</div>
          <h1>{{ visuals.name }}</h1>
        </div>
      </div>
    </section>

    <section class="checkout-body">
      <div class="checkout-summary">
        <div class="checkout-summary__card">
          <h2 class="checkout-summary__title">{{ $t('checkout.support_title') }}</h2>
          <div class="checkout-summary__details">
            <p v-if="visuals.description">{{ visuals.description }}</p>
            <p v-else>{{ $t('checkout.default_description') }}</p>
            <div v-if="socials.length" class="checkout-socials" :aria-label="$t('checkout.socials_label')">
              <a v-for="social in socials" :key="social.name" class="checkout-social" :href="social.href" target="_blank" rel="noopener">
                <img :src="social.icon" :alt="social.name" width="18" height="18">
                <span>{{ social.name }}</span>
              </a>
            </div>
          </div>
        </div>

        <form class="checkout-panel" @submit.prevent="submit">
          <div v-if="monthlyCheckoutAvailable" class="checkout-toggle" role="tablist" :aria-label="$t('checkout.payment_type_label')">
            <button type="button" :class="{ active: paymentKind === 'one_time' }" @click="paymentKind = 'one_time'">{{ $t('checkout.one_time') }}</button>
            <button type="button" :class="{ active: paymentKind === 'monthly' }" @click="paymentKind = 'monthly'">{{ $t('checkout.monthly') }}</button>
          </div>

          <div v-if="monthlyPaymentActive" class="checkout-current-payment">
            <strong>{{ $t('checkout.current_monthly_title') }}</strong>
            <p>{{ $t('checkout.current_monthly_text') }}</p>
          </div>

          <div v-else>
            <label class="checkout-label" for="checkout-amount">{{ $t('checkout.amount') }}</label>
            <div class="checkout-amount">
              <input id="checkout-amount" v-model.number="amount" type="number" :min="minAmount" :max="maxAmount" step="1" required>
              <span>{{ currency }}</span>
            </div>
            <div class="checkout-chips">
              <button v-for="preset in presets" :key="preset" type="button" @click="amount = preset">{{ preset }} {{ currency }}</button>
            </div>
            <div class="checkout-hint">{{ $t('checkout.min_amount', {amount: minAmount, currency}) }}</div>
          </div>

          <template v-if="paymentKind === 'one_time'">
            <label class="checkout-label">{{ $t('checkout.payment_method') }}</label>
            <div class="checkout-methods">
              <button v-for="method in paymentMethodOptions" :key="method.id" type="button" :class="{ active: paymentMethod === method.id }" @click="paymentMethod = method.id">
                <span>{{ method.label }}</span>
              </button>
            </div>
          </template>

          <template v-else>
            <div v-if="!authenticatedAsSubscriber" class="checkout-login">
              <p>{{ $t('checkout.monthly_login_text') }}</p>
              <SigninWithGoogle />
            </div>
            <div v-else class="checkout-login checkout-login--ready">
              <span>{{ $t('checkout.logged_in_google') }}</span>
              <a class="checkout-logout-link" href="/logout">{{ $t('logout') }}</a>
            </div>
          </template>

          <div v-if="errorMessage" class="checkout-error">{{ errorMessage }}</div>
          <button v-if="showSubmit" class="checkout-submit" type="submit" :disabled="submitDisabled">{{ submitText }}</button>
          <a v-if="hasCurrentMonthlyPayment" class="checkout-manage-link" href="/checkout/subscription">{{ $t('checkout.manage_monthly_payment') }}</a>
        </form>
      </div>
    </section>
  </main>
</template>

<script>
import {PostDonationEffect} from '@/api/api';
import SigninWithGoogle from '@/components/general/SigninWithGoogle.vue';

export default {
  components: {SigninWithGoogle},
  data() {
    return {
      paymentKind: 'one_time',
      paymentMethod: 'donation',
      amount: 0,
      inProgress: false,
      localError: '',
    };
  },
  computed: {
    visuals() {
      return this.$store.getters.visuals || {};
    },
    heroStyle() {
      if (!this.visuals.coverImage) return {};
      return {backgroundImage: `url("${this.visuals.coverImage}")`};
    },
    currency() {
      return this.$store.getters.channelCurrency || this.visuals.currency || 'USD';
    },
    minAmount() {
      return this.$store.getters.checkoutMinAmount || this.visuals.minDonation || 1;
    },
    maxAmount() {
      return this.visuals.maxDonation || 999999;
    },
    presets() {
      const base = [this.minAmount, this.minAmount * 2, this.minAmount * 5, this.minAmount * 10];
      const defaults = this.visuals.defaultDonation >= this.minAmount ? [this.visuals.defaultDonation] : [];
      return Array.from(new Set([...defaults, ...base])).filter(v => v <= this.maxAmount).slice(0, 4);
    },
    allowedPaymentMethods() {
      return this.$store.getters.allowedPaymentMethods || [];
    },
    cardAvailable() {
      return this.allowedPaymentMethods.includes('donation');
    },
    cryptoMethods() {
      return this.allowedPaymentMethods.filter(method => method !== 'donation');
    },
    paymentMethodOptions() {
      const methods = [];
      if (this.cardAvailable) {
        methods.push({id: 'donation', label: this.$t('checkout.card')});
      }
      this.cryptoMethods.forEach(symbol => {
        methods.push({id: symbol, label: symbol});
      });
      return methods;
    },
    socials() {
      const v = this.visuals || {};
      const items = [];
      if (v.twitter) items.push({name: 'Twitter', href: v.twitter, icon: require('@/assets/img/twitter.svg')});
      if (v.instagram) items.push({name: 'Instagram', href: v.instagram, icon: require('@/assets/img/instagram.svg')});
      if (v.telegram) items.push({name: 'Telegram', href: v.telegram, icon: require('@/assets/img/telegram.svg')});
      if (v.youtube) items.push({name: 'YouTube', href: v.youtube, icon: require('@/assets/img/youtube.svg')});
      if (v.twitch) items.push({name: 'Twitch', href: v.twitch, icon: require('@/assets/img/twitch.svg')});
      return items;
    },
    authenticatedAsSubscriber() {
      return this.$store.getters.authenticatedAsSubscriber;
    },
    currentSubscription() {
      return this.$store.getters.currentSubscription;
    },
    hasCurrentSubscription() {
      return this.hasCurrentMonthlyPayment;
    },
    hasCurrentMonthlyPayment() {
      return !!(this.currentSubscription && this.currentSubscription.recurrent);
    },
    monthlyPaymentActive() {
      return this.paymentKind === 'monthly' && this.hasCurrentMonthlyPayment;
    },
    monthlyCheckoutAvailable() {
      return this.cardAvailable && this.subscriptionsEnabled;
    },
    subscriptionsEnabled() {
      return this.$store.getters.subscriptionsEnabled;
    },
    errorMessage() {
      return this.localError || this.$store.getters.errorMessage;
    },
    submitDisabled() {
      return this.inProgress || this.amount < this.minAmount || this.amount > this.maxAmount || (this.paymentKind === 'one_time' && this.allowedPaymentMethods.length === 0) || (this.paymentKind === 'monthly' && (!this.authenticatedAsSubscriber || !this.subscriptionsEnabled));
    },
    submitText() {
      if (this.inProgress) return this.$t('checkout.going_to_payment');
      return this.paymentKind === 'monthly' ? this.$t('checkout.submit_monthly') : this.$t('checkout.submit_donation');
    },
    showSubmit() {
      return !this.monthlyPaymentActive;
    },
  },
  created() {
    this.applyDefaults();
    if (this.authenticatedAsSubscriber) {
      this.$store.dispatch('updateSubscription');
    }
  },
  watch: {
    minAmount() {
      this.applyDefaults();
    },
    allowedPaymentMethods() {
      this.applyDefaults();
    },
    monthlyCheckoutAvailable() {
      this.applyDefaults();
    },
    authenticatedAsSubscriber(value) {
      if (value) {
        this.$store.dispatch('updateSubscription');
      }
    },
  },
  methods: {
    applyDefaults() {
      const initial = this.visuals.defaultDonation || this.minAmount;
      if (!this.amount || this.amount < this.minAmount) {
        this.amount = Math.max(initial, this.minAmount);
      }
      if (!this.monthlyCheckoutAvailable) {
        this.paymentKind = 'one_time';
      }
      if (this.paymentMethod === 'donation' && !this.cardAvailable && this.cryptoMethods.length) {
        this.paymentMethod = this.cryptoMethods[0];
      }
      if (this.paymentMethod !== 'donation' && !this.allowedPaymentMethods.includes(this.paymentMethod)) {
        this.paymentMethod = this.cardAvailable ? 'donation' : this.cryptoMethods[0] || 'donation';
      }
    },
    validateAmount() {
      this.localError = '';
      if (!Number.isInteger(this.amount) || this.amount < this.minAmount) {
        this.localError = this.$t('checkout.min_amount_error', {amount: this.minAmount, currency: this.currency});
        return false;
      }
      if (this.amount > this.maxAmount) {
        this.localError = this.$t('checkout.max_amount_error', {amount: this.maxAmount, currency: this.currency});
        return false;
      }
      return true;
    },
    submit() {
      if (!this.validateAmount()) return;
      this.inProgress = true;
      if (this.paymentKind === 'monthly') {
        this.$store.dispatch('checkoutSubscribe', this.amount).finally(() => {
          this.inProgress = false;
        });
        return;
      }

      const paymentCurrency = this.paymentMethod === 'donation' ? this.currency : this.paymentMethod;
      PostDonationEffect(0, '', '', this.amount, this.currency, paymentCurrency).then((response) => {
        if (response.data?.error) {
          this.localError = response.data.error;
          return;
        }
        if (response.data?.redirect) {
          window.location.href = response.data.redirect;
          return;
        }
        if (response.data?.address) {
          this.$store.dispatch('startCryptoPayment', {...response.data, payment_currency: paymentCurrency, kind: 'donation'});
        }
      }).catch((error) => {
        this.localError = error.response?.data?.error || this.$t('checkout.payment_start_error');
      }).finally(() => {
        this.inProgress = false;
      });
    },
  },
};
</script>

<style scoped>
.checkout-page {
  min-height: 100vh;
  background: #f5f7f8;
  color: #17212b;
}
.checkout-hero {
  position: relative;
  min-height: 260px;
  background: #204f62;
  background-position: center;
  background-size: cover;
}
.checkout-hero__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(11, 31, 42, .82), rgba(11, 31, 42, .38));
}
.checkout-hero__content {
  position: relative;
  z-index: 1;
  max-width: 1040px;
  margin: 0 auto;
  min-height: 260px;
  padding: 32px 20px;
  display: flex;
  align-items: center;
  gap: 18px;
  color: #fff;
}
.checkout-hero__logo {
  width: 78px;
  height: 78px;
  object-fit: cover;
  border-radius: 8px;
  background: #fff;
}
.checkout-hero__label {
  font-size: 14px;
  opacity: .82;
}
.checkout-hero h1 {
  margin: 4px 0 0;
  font-size: 42px;
  line-height: 1.1;
  letter-spacing: 0;
}
.checkout-body {
  max-width: 1040px;
  margin: -42px auto 0;
  padding: 0 20px 56px;
  position: relative;
  z-index: 2;
}
.checkout-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 24px;
  align-items: start;
}
.checkout-summary__card,
.checkout-panel {
  background: #fff;
  border: 1px solid #dde3e8;
  border-radius: 8px;
  box-shadow: 0 16px 40px rgba(25, 39, 52, .08);
}
.checkout-summary__card {
  padding: 28px;
}
.checkout-summary__title {
  margin: 0 0 12px;
  font-size: 26px;
  letter-spacing: 0;
}
.checkout-summary p {
  margin: 0;
  color: #4c5a66;
  line-height: 1.55;
}
.checkout-manage-link {
  display: block;
  margin-top: 14px;
  color: var(--control-color, #1f6feb);
  text-align: center;
  text-decoration: none;
  font-weight: 400;
  font-size: 14px;
}
.checkout-manage-link:hover {
  text-decoration: underline;
}
.checkout-socials {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 22px;
}
.checkout-social {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  border: 1px solid #dde3e8;
  border-radius: 999px;
  padding: 0 12px;
  color: #17212b;
  text-decoration: none;
  background: #fff;
}
.checkout-social:hover {
  border-color: var(--control-color, #1f6feb);
  color: var(--control-color, #1f6feb);
}
.checkout-panel {
  padding: 22px;
  width: 100%;
  box-sizing: border-box;
}
.checkout-toggle,
.checkout-methods,
.checkout-chips {
  display: grid;
  gap: 8px;
}
.checkout-toggle {
  grid-template-columns: 1fr 1fr;
  margin-bottom: 18px;
}
.checkout-toggle button,
.checkout-methods button,
.checkout-chips button {
  border: 1px solid #cfd8df;
  background: #fff;
  border-radius: 6px;
  min-height: 42px;
  cursor: pointer;
}
.checkout-toggle button.active,
.checkout-methods button.active {
  border-color: var(--control-color, #1f6feb);
  background: #fff;
  box-shadow: inset 0 0 0 1px var(--control-color, #1f6feb);
  color: var(--control-color, #1f6feb);
}
.checkout-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}
.checkout-amount {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  border: 1px solid #cfd8df;
  border-radius: 6px;
  overflow: hidden;
}
.checkout-amount:focus-within {
  border-color: var(--control-color, #1f6feb);
  box-shadow: 0 0 0 2px rgba(31, 111, 235, .12);
}
.checkout-amount input {
  border: 0;
  min-height: 48px;
  padding: 0 14px;
  font-size: 24px;
  min-width: 0;
}
.checkout-amount span {
  padding: 0 14px;
  color: #4c5a66;
}
.checkout-chips {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 12px 0 8px;
}
.checkout-chips button:hover,
.checkout-chips button:focus {
  border-color: var(--control-color, #1f6feb);
  color: var(--control-color, #1f6feb);
}
.checkout-hint {
  color: #66737f;
  font-size: 13px;
  margin-bottom: 18px;
}
.checkout-current-payment {
  border: 1px solid var(--control-color, #1f6feb);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 18px;
}
.checkout-current-payment strong {
  display: block;
  color: #17212b;
  font-size: 16px;
  line-height: 1.35;
}
.checkout-current-payment p {
  margin: 8px 0 0;
  color: #4c5a66;
  line-height: 1.45;
}
.checkout-methods {
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  margin-bottom: 18px;
}
.checkout-methods button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.checkout-login {
  border: 1px solid #dde3e8;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 18px;
}
.checkout-login p {
  margin: 0 0 12px;
  color: #4c5a66;
}
.checkout-login--ready {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.checkout-logout-link {
  color: var(--control-color, #1f6feb);
  border: 1px solid var(--control-color, #1f6feb);
  border-radius: 6px;
  padding: 7px 12px;
  text-decoration: none;
  line-height: 1;
  white-space: nowrap;
}
.checkout-logout-link:hover {
  background: var(--control-color, #1f6feb);
  color: #fff;
}
.checkout-error {
  color: #b42318;
  margin-bottom: 12px;
}
.checkout-submit {
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 6px;
  background: var(--control-color, #16794c);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}
.checkout-submit:disabled {
  background: #98a5b1;
  cursor: default;
}
@media (max-width: 820px) {
  .checkout-hero {
    min-height: 220px;
  }
  .checkout-hero__content {
    align-items: center;
    min-height: 220px;
    padding: 28px 16px;
  }
  .checkout-hero__logo {
    width: 64px;
    height: 64px;
  }
  .checkout-hero h1 {
    font-size: 32px;
  }
  .checkout-body {
    margin: 16px auto 0;
    padding: 0 16px 40px;
  }
  .checkout-summary {
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #dde3e8;
    border-radius: 8px;
    box-shadow: 0 16px 40px rgba(25, 39, 52, .08);
    padding: 20px;
  }
  .checkout-summary__card {
    display: contents;
  }
  .checkout-summary__title {
    order: 1;
    margin-bottom: 18px;
  }
  .checkout-panel {
    order: 2;
    align-self: stretch;
    background: transparent;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    padding: 0;
  }
  .checkout-summary__details {
    order: 3;
    margin-top: 4px;
  }
  .checkout-login--ready {
    align-items: flex-start;
  }
}
@media (max-width: 480px) {
  .checkout-hero__content {
    gap: 14px;
  }
  .checkout-hero h1 {
    font-size: 28px;
  }
  .checkout-toggle {
    grid-template-columns: 1fr;
  }
}
</style>
